import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Slider,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Grid,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Badge,
  FormControlLabel,
  Switch,
  Stack,
  Skeleton,
  Drawer,
  Fab,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Description as FileIcon,
  Visibility as PreviewIcon,
  Help as HelpIcon,
  MonetizationOn as CostIcon,
  Speed as PerformanceIcon,
  TrendingUp as QualityIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  PlayArrow as ProcessIcon,
  CheckCircle as CompleteIcon,
  Error as ErrorIcon,
  InfoOutlined as InfoIcon,
  AutoAwesome as AutoIcon,
  Build as BuildIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ragService } from '../services/ragService';

const DocumentUpload = () => {
  const { kbId } = useParams();
  const navigate = useNavigate();
  
  // Estado principal
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadStep, setUploadStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Configurações de chunking do backend
  const [supportedStrategies, setSupportedStrategies] = useState([]);
  const [loadingStrategies, setLoadingStrategies] = useState(true);
  
  // Configuração ativa
  const [chunkingConfig, setChunkingConfig] = useState({
    strategy: 'recursive',
    chunk_size: 500,
    chunk_overlap: 50,
    chunker_type: 'recursive',
    separators: ['\n\n', '\n', ' ', ''],
    note: ''
  });
  const [customConfig, setCustomConfig] = useState(false);
  
  // Preview em tempo real
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [autoPreview, setAutoPreview] = useState(true);
  
  // Estado de robustez
  const [chunkingHealth, setChunkingHealth] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  
  // Estado para drawer de preview
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  
  // Estado para modal de chunk detalhado
  const [chunkDetailOpen, setChunkDetailOpen] = useState(false);
  const [selectedChunk, setSelectedChunk] = useState(null);

  // Carregar dados iniciais
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Carregar KB
        if (kbId) {
          const kb = await ragService.getKnowledgeBase(kbId);
          setKnowledgeBase(kb);
        }

        // Carregar estratégias do backend
        const strategiesData = await ragService.getChunkingStrategies();
        setSupportedStrategies(strategiesData.supported_strategies);

        // Verificar saúde do chunking
        const health = await ragService.checkChunkingHealth();
        setChunkingHealth(health);

        setLoadingStrategies(false);
      } catch (err) {
        setError('Erro ao carregar configurações: ' + err.message);
        setLoadingStrategies(false);
        
        // Em caso de erro, carregar informações de debug
        try {
          const debug = await ragService.testChunkingSystem();
          setDebugInfo(debug);
        } catch (debugErr) {
          console.error('Erro ao carregar debug:', debugErr);
        }
      }
    };
    
    loadInitialData();
  }, [kbId]);

  // Função para mostrar debug
  const handleShowDebug = async () => {
    try {
      const debug = await ragService.testChunkingSystem();
      setDebugInfo(debug);
      setShowDebug(true);
    } catch (err) {
      console.error('Erro ao carregar debug:', err);
    }
  };

  // Handler para seleção de arquivos
  const handleFileSelect = useCallback(async (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    setUploadStep(1);
    setError(null);
    
    if (files.length > 0) {
      const firstFile = files[0];
      setPreviewFile(firstFile);
      
      // Obter recomendação automática
      try {
        const recommendation = await ragService.getChunkingRecommendation({
          name: firstFile.name,
          size: firstFile.size,
          type: firstFile.type,
          use_case: 'general'
        });
        
        // Auto-selecionar estratégia baseada na extensão
        const filename = firstFile.name.toLowerCase();
        const extension = filename.split('.').pop();
        let finalConfig = { ...recommendation.recommended_config };
        
        if (extension === 'csv') {
          finalConfig.strategy = 'fixed_size';
        } else if (extension === 'md' || extension === 'markdown') {
          finalConfig.strategy = 'markdown';
        }
        
        // Aplicar configuração recomendada
        setChunkingConfig(finalConfig);
        
        // Gerar preview se auto-preview habilitado
        if (autoPreview) {
          handleGeneratePreview(firstFile, finalConfig);
        }
      } catch (err) {
        console.warn('Falha ao obter recomendação:', err);
        if (autoPreview) {
          handleGeneratePreview(firstFile, chunkingConfig);
        }
      }
    }
  }, [autoPreview, chunkingConfig]);

  // Handler para mudança de estratégia
  const handleStrategyChange = useCallback((strategyValue) => {
    // Definir separadores específicos para cada estratégia
    let separators;
    if (strategyValue === 'markdown') {
      // Para markdown: separadores são para RecursiveCharacterTextSplitter (etapa 2)
      // Headers (##, ###) são configurados automaticamente no backend
      separators = ['\n# ', '\n## ', '\n### ', '\n\n', '\n', ' ', ''];
    } else if (strategyValue === 'recursive') {
      separators = ['\n\n', '\n', ' ', ''];
    } else {
      separators = ['\n\n', '\n', ' ', ''];
    }
    
    const newConfig = { 
      ...chunkingConfig, 
      strategy: strategyValue,
      chunker_type: strategyValue,
      separators: separators
    };
    setChunkingConfig(newConfig);
    setCustomConfig(true);
    
    // Regerar preview
    if (previewFile && autoPreview) {
      handleGeneratePreview(previewFile, newConfig);
    }
  }, [chunkingConfig, previewFile, autoPreview]);

  // Filtrar estratégias baseado no tipo de arquivo
  const getAvailableStrategies = useCallback(() => {
    if (!previewFile) return supportedStrategies;
    
    const filename = previewFile.name.toLowerCase();
    const extension = filename.split('.').pop();
    
    // Filtrar estratégias baseado na extensão
    return supportedStrategies.filter(strategy => {
      if (extension === 'csv') {
        return strategy.value === 'fixed_size'; // Apenas CSV para arquivos CSV
      } else if (extension === 'md' || extension === 'markdown') {
        return ['recursive', 'markdown'].includes(strategy.value); // Recursive ou Markdown para .md
      } else if (extension === 'pdf' || extension === 'txt') {
        return ['recursive', 'markdown'].includes(strategy.value); // Recursive ou Markdown para PDF/TXT
      }
      return true; // Mostrar todas se não identificar o tipo
    });
  }, [supportedStrategies, previewFile]);

  // Handler para configuração customizada
  const handleConfigChange = useCallback((field, value) => {
    const newConfig = { ...chunkingConfig, [field]: value };
    setChunkingConfig(newConfig);
    setCustomConfig(true);
    
    // Regerar preview com debounce
    if (previewFile && autoPreview) {
      clearTimeout(window.previewTimeout);
      window.previewTimeout = setTimeout(() => {
        handleGeneratePreview(previewFile, newConfig);
      }, 800);
    }
  }, [chunkingConfig, previewFile, autoPreview]);

  // Gerar preview de chunks usando backend real
  const handleGeneratePreview = async (file, config = chunkingConfig) => {
    if (!file) return;
    
    try {
      setPreviewLoading(true);
      setError(null);
      
      const previewResponse = await ragService.previewDocumentChunks(file, {
        chunker_type: config.strategy,
        chunk_size: config.chunk_size,
        chunk_overlap: config.chunk_overlap,
        separators: config.separators,
        semanticConfig: config.semanticConfig,
        max_preview_chunks: 15
      });
      
      setPreviewData(previewResponse);
      
    } catch (err) {
      setError('Erro ao gerar preview: ' + err.message);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Funções do modal de chunk detalhado
  const handleOpenChunkDetail = (chunk) => {
    setSelectedChunk(chunk);
    setChunkDetailOpen(true);
  };

  const handleCloseChunkDetail = () => {
    setChunkDetailOpen(false);
    setSelectedChunk(null);
  };

  // Função para formatação amigável das métricas
  const formatMetrics = (previewData) => {
    if (!previewData?.quality_metrics) return null;

    const metrics = previewData.quality_metrics;
    const totalTokens = previewData.total_tokens || 0;
    
    // Calcular consistência baseada na variância
    const variance = metrics.token_variance || 0;
    const avgTokens = metrics.avg_token_count || 0;
    const coefficientVariation = avgTokens > 0 ? Math.sqrt(variance) / avgTokens : 0;
    
    let consistencyLevel = "Excelente";
    let consistencyColor = "success";
    if (coefficientVariation > 0.5) {
      consistencyLevel = "Baixa";
      consistencyColor = "error";
    } else if (coefficientVariation > 0.3) {
      consistencyLevel = "Moderada";
      consistencyColor = "warning";
    } else if (coefficientVariation > 0.15) {
      consistencyLevel = "Boa";
      consistencyColor = "info";
    }

    // Calcular custo em centavos para melhor compreensão
    const costInCents = (previewData.estimated_cost || 0) * 100;
    const costInBRL = costInCents * 5.5; // Aproximação de conversão USD -> BRL

    return {
      quality: {
        score: Math.round((metrics.avg_quality || 0.85) * 100),
        level: (metrics.avg_quality || 0.85) > 0.8 ? "Excelente" : 
               (metrics.avg_quality || 0.85) > 0.6 ? "Boa" : "Precisa Melhorar",
        color: (metrics.avg_quality || 0.85) > 0.8 ? "success" : 
               (metrics.avg_quality || 0.85) > 0.6 ? "warning" : "error"
      },
      consistency: {
        level: consistencyLevel,
        color: consistencyColor,
        variance: Math.round(variance),
        explanation: coefficientVariation < 0.15 ? 
          "Chunks têm tamanhos muito similares" :
          coefficientVariation < 0.3 ?
          "Chunks têm tamanhos razoavelmente similares" :
          "Chunks têm tamanhos muito variados"
      },
      cost: {
        usd: previewData.estimated_cost || 0,
        cents: Math.round(costInCents * 100) / 100,
        brl: Math.round(costInBRL * 100) / 100,
        tokens: totalTokens
      },
      tokens: {
        total: totalTokens,
        average: Math.round(avgTokens),
        perChunk: Math.round(totalTokens / Math.max(1, previewData.total_chunks))
      }
    };
  };

  // Processar upload final
  const handleFinalUpload = async () => {
    try {
      setProcessing(true);
      setError(null);
      setUploadStep(2);
      
      for (const file of selectedFiles) {
        await ragService.uploadDocument(
          kbId,
          file,
          {
            chunk_size: chunkingConfig.chunk_size,
            chunk_overlap: chunkingConfig.chunk_overlap,
            // ✅ CORRIGIR nome dos campos para match com backend
            chunker_type: chunkingConfig.strategy,
            separators: chunkingConfig.separators
          },
          (progress) => {
            console.log(`Upload progress: ${progress}%`);
          }
        );
      }
      
      setUploadStep(3);
      setSuccess(true);
      
      // Redirecionar para a lista de documentos da KB após sucesso
      setTimeout(() => {
        navigate(`/knowledge-base/${kbId}`);
      }, 2000);
      
    } catch (err) {
      setError('Erro no upload: ' + err.message);
      setProcessing(false);
      setUploadStep(1);
    }
  };

  // Componente para exibir badges de robustez
  const RobustnessBadges = () => (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <MuiTooltip title="Sistema de chunking avançado">
        <Chip 
          icon={<BuildIcon />} 
          label="Chunking Avançado" 
          size="small" 
          color="primary" 
          variant="outlined" 
        />
      </MuiTooltip>
      <MuiTooltip title="Contagem precisa de tokens com tiktoken">
        <Chip 
          icon={<SpeedIcon />} 
          label="Token-Aware" 
          size="small" 
          color="secondary" 
          variant="outlined" 
        />
      </MuiTooltip>

      {chunkingHealth?.status === 'healthy' && (
        <MuiTooltip title="Sistema de chunking operacional">
          <Chip 
            icon={<SecurityIcon />} 
            label="Operational" 
            size="small" 
            color="success" 
          />
        </MuiTooltip>
      )}
    </Stack>
  );

  // Componente de Preview Sidebar
  const PreviewSidebar = () => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          {/* Removido o header duplicado - agora está no drawer header */}
        </Box>
        
        {!previewFile && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <InfoIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="body2">
              Selecione um arquivo para ver o preview dos chunks
            </Typography>
          </Box>
        )}
        
        {previewLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Gerando preview...
            </Typography>
          </Box>
        )}
        
        {previewData && !previewLoading && (
          <Box>
            {/* Resumo Visual Rápido */}
            {(() => {
              const metrics = formatMetrics(previewData);
              return metrics ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QualityIcon color="primary" fontSize="small" />
                      <Typography variant="body2">
                        <strong>{metrics.quality.score}%</strong> de qualidade
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PerformanceIcon color="primary" fontSize="small" />
                      <Typography variant="body2">
                        <strong>{previewData.total_chunks}</strong> chunks • <strong>{metrics.tokens.total}</strong> tokens
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CostIcon color="primary" fontSize="small" />
                      <Typography variant="body2">
                        <strong>R$ {metrics.cost.brl.toFixed(3)}</strong> estimado
                      </Typography>
                    </Box>
                  </Stack>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{previewData.total_chunks} chunks</strong> • 
                    <strong>{previewData.total_tokens} tokens</strong> • 
                    <strong>${previewData.estimated_cost?.toFixed(4) || '0.0000'}</strong>
                  </Typography>
                </Alert>
              );
            })()}

            {/* Análise do documento */}
            {previewData.document_analysis && (
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Análise do Documento</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    <Typography variant="caption">
                      <strong>Idioma:</strong> {previewData.document_analysis.detected_language}
                    </Typography>
                    <Typography variant="caption">
                      <strong>Tamanho:</strong> {(previewData.document_analysis.file_size / 1024).toFixed(1)}KB
                    </Typography>
                    <Typography variant="caption">
                      <strong>Chunker Recomendado:</strong> {previewData.document_analysis.recommended_chunker}
                    </Typography>
                    <Typography variant="caption">
                      <strong>Tempo de Processamento:</strong> {previewData.processing_time?.toFixed(2)}s
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Métricas de Qualidade - Versão Amigável */}
            {(() => {
              const metrics = formatMetrics(previewData);
              return metrics && previewData.quality_metrics ? (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        Métricas de Qualidade
                      </Typography>
                      <Chip 
                        label={`${metrics.quality.score}%`}
                        size="small" 
                        color={metrics.quality.color}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {/* Qualidade */}
                      <Grid item xs={12} sm={4}>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                            <QualityIcon color={metrics.quality.color} />
                            <MuiTooltip title="Baseado na estrutura, tamanho ideal e consistência dos chunks. Acima de 80% é excelente.">
                              <HelpIcon fontSize="small" color="action" />
                            </MuiTooltip>
                          </Box>
                          <Typography variant="h6" color={`${metrics.quality.color}.main`}>
                            {metrics.quality.score}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Qualidade {metrics.quality.level}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Consistência */}
                      <Grid item xs={12} sm={4}>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                            <PerformanceIcon color={metrics.consistency.color} />
                            <MuiTooltip title="Mede quão similar são os tamanhos dos chunks. Chunks consistentes facilitam o processamento.">
                              <HelpIcon fontSize="small" color="action" />
                            </MuiTooltip>
                          </Box>
                          <Typography variant="h6" color={`${metrics.consistency.color}.main`}>
                            {metrics.consistency.level}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {metrics.consistency.explanation}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Custo */}
                      <Grid item xs={12} sm={4}>
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                            <CostIcon color="primary" />
                            <MuiTooltip title="Custo estimado para gerar embeddings usando o modelo text-embedding-3-small ($0.02 por 1M tokens)">
                              <HelpIcon fontSize="small" color="action" />
                            </MuiTooltip>
                          </Box>
                          <Typography variant="h6" color="primary.main">
                            R$ {metrics.cost.brl.toFixed(3)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ≈ ${metrics.cost.usd.toFixed(4)} USD
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Detalhes Técnicos */}
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          💡 <strong>Dica:</strong> Chunks de tamanho consistente (200-800 tokens) geralmente produzem melhores resultados de busca semântica.
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ) : previewData.quality_metrics ? (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Métricas de Qualidade
                      <Chip 
                        label={`${(previewData.quality_metrics.avg_quality * 100).toFixed(0)}%`}
                        size="small" 
                        color="primary" 
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      <Typography variant="caption">
                        <strong>Qualidade Média:</strong> {(previewData.quality_metrics.avg_quality * 100).toFixed(1)}%
                      </Typography>
                      <Typography variant="caption">
                        <strong>Tokens por Chunk:</strong> {previewData.quality_metrics.avg_token_count?.toFixed(0)}
                      </Typography>
                      <Typography variant="caption">
                        <strong>Variância de Tokens:</strong> {previewData.quality_metrics.token_variance?.toFixed(0)}
                      </Typography>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ) : null;
            })()}
            
            {/* Lista de chunks */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Chunks Preview ({previewData.chunks_preview.length}/{previewData.total_chunks})
            </Typography>
            
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              {previewData.chunks_preview.map((chunk, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{ 
                    p: 2, 
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: 2,
                      bgcolor: 'action.hover',
                      transform: 'translateY(-1px)'
                    }
                  }}
                  onClick={() => handleOpenChunkDetail(chunk)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`#${chunk.index}`}
                        size="small"
                        color="primary"
                      />
                      <Chip
                        label="👁️ Clique para ver completo"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', color: 'primary.main' }}
                      />
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <MuiTooltip title={`${chunk.token_count} tokens (contados com tiktoken)`}>
                        <Chip
                          label={`${chunk.token_count}t`}
                          size="small"
                          variant="outlined"
                        />
                      </MuiTooltip>
                      <MuiTooltip title={`Qualidade do chunk: ${(chunk.quality_score * 100).toFixed(0)}%`}>
                        <Chip
                          label={`${(chunk.quality_score * 100).toFixed(0)}%`}
                          size="small"
                          color={chunk.quality_score > 0.8 ? 'success' : chunk.quality_score > 0.6 ? 'warning' : 'error'}
                        />
                      </MuiTooltip>
                    </Stack>
                  </Box>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      color: 'text.secondary',
                      maxHeight: 180,
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      backgroundColor: 'grey.50',
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      position: 'relative',
                      '&::after': chunk.content.length > 600 ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '30px',
                        background: 'linear-gradient(transparent, rgba(245, 245, 245, 0.9))',
                        pointerEvents: 'none'
                      } : {}
                    }}
                  >
                    {chunk.content.length > 600 
                      ? chunk.content.substring(0, 600) + '...'
                      : chunk.content
                    }
                  </Typography>
                </Paper>
              ))}
              
              {previewData.total_chunks > previewData.chunks_preview.length && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                  ... e mais {previewData.total_chunks - previewData.chunks_preview.length} chunks
                </Typography>
              )}
            </Box>

            {/* Botão para regerar preview */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => handleGeneratePreview(previewFile)}
                disabled={previewLoading}
              >
                Atualizar Preview
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate(`/knowledge-base/${kbId}`)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
              Upload de Documentos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {knowledgeBase?.name} • Processamento avançado de chunks
            </Typography>
          </Box>
        </Box>
        
        {/* Badges de robustez */}
        <RobustnessBadges />
      </motion.div>

      {/* Stepper de progresso */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={uploadStep} alternativeLabel>
            <Step>
              <StepLabel>Selecionar Arquivos</StepLabel>
            </Step>
            <Step>
              <StepLabel>Configurar Chunking</StepLabel>
            </Step>
            <Step>
              <StepLabel>Upload e Indexação</StepLabel>
            </Step>
            <Step>
              <StepLabel>Concluído</StepLabel>
            </Step>
          </Stepper>
        </CardContent>
      </Card>

      {/* Layout principal - Largura total */}
      <Box sx={{ position: 'relative' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
            {/* Seleção de arquivos */}
            {uploadStep === 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <UploadIcon sx={{ mr: 1 }} />
                    Selecionar Arquivos
                  </Typography>
                  
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.dark'
                      }
                    }}
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Clique para selecionar arquivos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Suporte: PDF, TXT, CSV, MD • Máximo 100MB por arquivo
                    </Typography>
                    
                    <input
                      id="file-input"
                      type="file"
                      multiple
                      accept=".pdf,.txt,.csv,.md"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Arquivos selecionados */}
            {selectedFiles.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <FileIcon sx={{ mr: 1 }} />
                    Arquivos Selecionados ({selectedFiles.length})
                  </Typography>
                  
                  <List dense>
                    {selectedFiles.map((file, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB • ${file.type || 'Unknown'}`}
                        />
                        <Chip
                          label={file.name.split('.').pop()?.toUpperCase() || 'UNK'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Configurações de Chunking */}
            {uploadStep >= 1 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ mr: 1 }} />
                    Configurações de Chunking
                    {chunkingHealth?.status === 'healthy' && (
                      <Chip 
                        label="Sistema Operacional" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Typography>
                  
                  {loadingStrategies ? (
                    <Box>
                      <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
                      <Skeleton variant="rectangular" height={200} />
                    </Box>
                  ) : (
                    <>
                      {/* Estratégias do Backend */}
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Estratégias Disponíveis
                        <Chip 
                          label={`${supportedStrategies.length} disponíveis`}
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      
                      <Grid container spacing={1} sx={{ mb: 3 }}>
                        {getAvailableStrategies().map((strategy) => (
                          <Grid item xs={6} sm={4} key={strategy.value}>
                            <Card
                              sx={{
                                cursor: 'pointer',
                                border: chunkingConfig.strategy === strategy.value ? 2 : 1,
                                borderColor: chunkingConfig.strategy === strategy.value ? 'primary.main' : 'divider',
                                '&:hover': { borderColor: 'primary.main' }
                              }}
                              onClick={() => handleStrategyChange(strategy.value)}
                            >
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {strategy.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {strategy.description}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={strategy.value}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 16 }}
                                  />
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      {/* Nota especial para CSV */}
                      {previewFile && previewFile.name.toLowerCase().endsWith('.csv') && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Arquivo CSV detectado:</strong> Cada linha do CSV será processada como um chunk individual. 
                            As configurações de tamanho e sobreposição não se aplicam a arquivos CSV.
                          </Typography>
                        </Alert>
                      )}
                      
                      {/* Configurações manuais */}
                      <Accordion expanded={customConfig}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          onClick={() => setCustomConfig(!customConfig)}
                        >
                          <Typography variant="subtitle2">
                            Configurações Avançadas
                            {customConfig && (
                              <Chip 
                                label="Personalizado" 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1 }} 
                              />
                            )}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>

                          
                          {/* Explicação específica para Markdown */}
                          {chunkingConfig.strategy === 'markdown' && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                <strong>Modo Markdown:</strong> O documento será dividido exatamente nos separadores especificados. 
                                Use <code>##</code>, <code>###</code> para quebrar em cabeçalhos específicos.
                              </Typography>
                            </Alert>
                          )}
                          
                          {/* Configurações de chunk apenas para não-CSV, não-Markdown e não-fixed_size */}
                          {!(previewFile && previewFile.name.toLowerCase().endsWith('.csv')) && 
                           chunkingConfig.strategy !== 'markdown' && 
                           chunkingConfig.strategy !== 'fixed_size' && (
                            <>
                              {/* Tamanho do chunk */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">
                                  Tamanho do Chunk: {chunkingConfig.chunk_size} tokens
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  (~{Math.round(chunkingConfig.chunk_size * 4)} caracteres)
                                </Typography>
                              </Box>
                              {chunkingConfig.chunk_size > 8192 && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                  ⚠️ Chunks com mais de 8.192 tokens podem ser truncados para embeddings
                                </Alert>
                              )}
                              <Slider
                                value={chunkingConfig.chunk_size}
                                onChange={(e, value) => handleConfigChange('chunk_size', value)}
                                min={100}
                                max={4000}
                                step={100}
                                marks={[
                                  { value: 500, label: '500' },
                                  { value: 1000, label: '1K' },
                                  { value: 2000, label: '2K' },
                                  { value: 4000, label: '4K' }
                                ]}
                                sx={{ mb: 3 }}
                              />
                              
                              {/* Sobreposição */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2">
                                  Sobreposição: {chunkingConfig.chunk_overlap} tokens
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  (~{Math.round(chunkingConfig.chunk_overlap * 4)} caracteres)
                                </Typography>
                              </Box>
                              <Slider
                                value={chunkingConfig.chunk_overlap}
                                onChange={(e, value) => handleConfigChange('chunk_overlap', value)}
                                min={0}
                                max={Math.floor(chunkingConfig.chunk_size * 0.5)}
                                step={25}
                                marks={[
                                  { value: 0, label: '0' },
                                  { value: Math.floor(chunkingConfig.chunk_size * 0.1), label: '10%' },
                                  { value: Math.floor(chunkingConfig.chunk_size * 0.25), label: '25%' },
                                  { value: Math.floor(chunkingConfig.chunk_size * 0.5), label: '50%' }
                                ]}
                                sx={{ mb: 3 }}
                              />
                            </>
                          )}
                          
                          {/* Separadores - Ocultar para CSV */}
                          {chunkingConfig.strategy !== 'fixed_size' && (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  Separadores (separados por vírgula)
                                </Typography>
                                <MuiTooltip 
                                  title={
                                    <Box sx={{ p: 1 }}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        🎯 Como funcionam os Separadores:
                                      </Typography>
                                      
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Separadores Padrão:</strong> <code>,,, !, ?, ,</code>
                                      </Typography>
                                      
                                      <Typography variant="body2" sx={{ mb: 1 }}>
                                        • <code>,,</code> = Quebras muito fortes (raramente usado)<br/>
                                        • <code>, </code> = Quebras médias (vírgula + espaço)<br/>
                                        • <code>!</code> = Fim de exclamações<br/>
                                        • <code>?</code> = Fim de perguntas<br/>
                                        • <code>,</code> = Qualquer vírgula
                                      </Typography>
                                      
                                      <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>
                                        <strong>📊 Exemplo Prático:</strong>
                                      </Typography>
                                      
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 1 }}>
                                        • Texto chegando perto de 2000 tokens<br/>
                                        • Sistema encontra vírgula boa em 1.800 tokens<br/>
                                        • Próxima vírgula boa só em 2.300 tokens<br/>
                                        • <strong>Decisão:</strong> Quebra em 1.800 para não passar do limite
                                      </Typography>
                                      
                                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                        ✅ Resultado: Chunks menores, mas com texto coerente!
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                  sx={{ maxWidth: 400 }}
                                >
                                  <IconButton size="small">
                                    <HelpIcon fontSize="small" />
                                  </IconButton>
                                </MuiTooltip>
                              </Box>
                              
                              <TextField
                                fullWidth
                                value={Array.isArray(chunkingConfig.separators) 
                                  ? chunkingConfig.separators.join(', ') 
                                  : chunkingConfig.separators
                                }
                                onChange={(e) => {
                                  const separators = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                                  handleConfigChange('separators', separators);
                                }}
                                sx={{ mb: 2 }}
                                helperText={
                                  chunkingConfig.strategy === 'markdown' 
                                    ? "Para Markdown: ##, ###, \\n\\n (quebra exatamente nos separadores especificados)"
                                    : "Ex: \\n\\n, \\n, ' ', '' (quebras de parágrafo hierárquicas - recomendado para chunks maiores)"
                                }
                                placeholder={
                                  chunkingConfig.strategy === 'markdown'
                                    ? "##, ###, \\n\\n"
                                    : "\\n\\n, \\n, ., !"
                                }
                              />
                            </>
                          )}
                          



                        </AccordionDetails>
                      </Accordion>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Botões de ação */}
            {uploadStep >= 1 && uploadStep < 3 && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {uploadStep === 1 && (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedFiles([]);
                            setUploadStep(0);
                            setPreviewData(null);
                            setPreviewFile(null);
                          }}
                          sx={{ flex: 1 }}
                        >
                          Voltar
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<ProcessIcon />}
                          onClick={handleFinalUpload}
                          disabled={processing || !previewData}
                          sx={{ flex: 2 }}
                        >
                          {processing ? 'Processando...' : 'Processar Documentos'}
                        </Button>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
        </motion.div>
      </Box>
      
      {/* Status de processamento */}
      <AnimatePresence>
        {uploadStep === 2 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Processamento Avançado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Extraindo conteúdo, criando chunks com estratégia {chunkingConfig.strategy}, gerando embeddings...
                  </Typography>
                  <LinearProgress sx={{ mt: 2, width: '60%', mx: 'auto' }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        )}
        
        {uploadStep === 3 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CompleteIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Upload Concluído!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedFiles.length} documento(s) processado(s) com chunking {chunkingConfig.strategy}
                  </Typography>
                  {previewData && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {previewData.total_chunks} chunks • {previewData.total_tokens} tokens • ${previewData.estimated_cost.toFixed(4)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Redirecionando para a base de conhecimento...
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              onClick={handleShowDebug}
              variant="outlined"
              color="inherit"
            >
              Mostrar Informações de Debug
            </Button>
          </Box>
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Documentos processados com sucesso usando {chunkingConfig.strategy} chunking!
        </Alert>
      )}

      {/* Debug Information */}
      {(showDebug || debugInfo) && debugInfo && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <BuildIcon sx={{ mr: 1 }} />
              Informações de Debug do Sistema
              <Chip 
                label={debugInfo.overall_status} 
                color={debugInfo.overall_status === 'success' ? 'success' : 'error'}
                sx={{ ml: 2 }}
              />
            </Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Testes do Sistema</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {debugInfo.tests && Object.entries(debugInfo.tests).map(([testName, testResult]) => (
                    <Paper key={testName} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                          {testName.replace('_', ' ')}
                        </Typography>
                        <Chip 
                          label={testResult.status}
                          size="small"
                          color={testResult.status === 'success' ? 'success' : 'error'}
                          sx={{ ml: 2 }}
                        />
                      </Box>
                      
                      {testResult.status === 'success' ? (
                        <Box>
                          {testResult.count && (
                            <Typography variant="caption" display="block">
                              Contagem: {testResult.count}
                            </Typography>
                          )}
                          {testResult.preset_names && (
                            <Typography variant="caption" display="block">
                              Presets: {testResult.preset_names.join(', ')}
                            </Typography>
                          )}
                          {testResult.types && (
                            <Typography variant="caption" display="block">
                              Tipos: {testResult.types.join(', ')}
                            </Typography>
                          )}
                          {testResult.token_count && (
                            <Typography variant="caption" display="block">
                              Teste de token: "{testResult.test_text}" = {testResult.token_count} tokens
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="error">
                          Erro: {testResult.error}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
            
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Button 
                onClick={() => setShowDebug(false)}
                variant="outlined"
                size="small"
              >
                Fechar Debug
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Botão Flutuante para Preview */}
      {(selectedFiles.length > 0 || previewData) && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            transform: 'translateY(-50%)',
            right: previewDrawerOpen ? { xs: 24, sm: '520px' } : 24, // Afastar quando drawer aberto
            zIndex: 1200,
            transition: 'right 0.3s ease',
          }}
        >
          <MuiTooltip title="Ver preview de chunks" placement="left">
            <Fab
              onClick={() => setPreviewDrawerOpen(!previewDrawerOpen)}
              size="medium"
              variant="extended"
              sx={{
                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                bgcolor: '#9c27b0', // Roxo igual ao botão testar
                color: 'white',
                display: { xs: previewDrawerOpen ? 'none' : 'flex', sm: 'flex' },
                '&:hover': {
                  bgcolor: '#7b1fa2',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <PreviewIcon sx={{ mr: 1 }} />
              {previewDrawerOpen ? "Fechar" : "Preview Chunks"}
            </Fab>
          </MuiTooltip>
        </Box>
      )}

      {/* Drawer de Preview */}
      <Drawer
        anchor="right"
        open={previewDrawerOpen}
        onClose={() => setPreviewDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '600px', md: '750px', lg: '800px' },
            maxWidth: '50vw',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
        transitionDuration={300}
      >
        {/* Header do Drawer */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Preview de Chunks
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {previewFile?.name || 'Nenhum arquivo selecionado'}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setPreviewDrawerOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        {/* Subtítulo */}
        <Box sx={{ 
          px: 2, 
          py: 1, 
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography variant="body2" color="text.secondary">
            🔍 Visualize como seu documento será dividido em chunks
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={autoPreview}
                onChange={(e) => setAutoPreview(e.target.checked)}
                size="small"
              />
            }
            label="Auto"
            sx={{ fontSize: '0.8rem' }}
          />
        </Box>

        {/* Conteúdo do Preview */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'auto',
        }}>
          <PreviewSidebar />
        </Box>
      </Drawer>

      {/* Modal de Detalhes do Chunk */}
      <Dialog
        open={chunkDetailOpen}
        onClose={handleCloseChunkDetail}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            height: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Detalhes do Chunk #{selectedChunk?.index || 0}
            </Typography>
            <Stack direction="row" spacing={1}>
              {selectedChunk && (
                <>
                  <Chip
                    label={`${selectedChunk.token_count} tokens`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${(selectedChunk.quality_score * 100).toFixed(0)}% qualidade`}
                    size="small"
                    color={selectedChunk.quality_score > 0.8 ? 'success' : selectedChunk.quality_score > 0.6 ? 'warning' : 'error'}
                  />
                </>
              )}
            </Stack>
          </Box>
          <IconButton onClick={handleCloseChunkDetail} size="small">
            <ArrowBackIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {selectedChunk && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Metadados do Chunk */}
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.default',
                borderBottom: 1,
                borderColor: 'divider'
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Tamanho em Caracteres
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedChunk.content.length.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Tokens (tiktoken)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedChunk.token_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Score de Qualidade
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {(selectedChunk.quality_score * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Posição no Documento
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      #{selectedChunk.index}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Conteúdo Completo do Chunk */}
              <Box sx={{ 
                flexGrow: 1, 
                p: 2,
                overflow: 'auto'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Conteúdo Completo:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '100%',
                    overflow: 'auto'
                  }}
                >
                  {selectedChunk.content}
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={handleCloseChunkDetail} variant="outlined">
            Fechar
          </Button>
          {selectedChunk && previewData && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                disabled={selectedChunk.index === 0}
                onClick={() => {
                  const prevChunk = previewData.chunks_preview.find(c => c.index === selectedChunk.index - 1);
                  if (prevChunk) setSelectedChunk(prevChunk);
                }}
              >
                ← Anterior
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={selectedChunk.index === previewData.chunks_preview[previewData.chunks_preview.length - 1]?.index}
                onClick={() => {
                  const nextChunk = previewData.chunks_preview.find(c => c.index === selectedChunk.index + 1);
                  if (nextChunk) setSelectedChunk(nextChunk);
                }}
              >
                Próximo →
              </Button>
            </Stack>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DocumentUpload; 